/**
 * What the AI tools add when they call trace-viewer's authoring operations:
 * the scene folder a copy-on-edit uploads into, the page-editing mode, a
 * refusal in the model's words, and a report of files a copy could not bring
 * across.
 */

import type { AuthoringPorts, Refusal } from 'trace-viewer/editor';
import type { ToolContext } from '../agent-helpers';
import type { ToolDefinition } from './types';
import { getActiveCmsManager } from '../../cms/active-manager';
import { pageEditing } from '../../page-editing-mode';

/**
 * Where a copy-on-edit puts a reference's files (the open CMS scene's folder,
 * when there is one), and whether an edit to a page's shared content goes to
 * that page, as a gizmo drag does.
 */
export function authoringPorts(): AuthoringPorts {
    const folder = getActiveCmsManager()?.folderUploadTarget();
    return folder ? { folder, pageEditing } : { pageEditing };
}

/** Say which files a copy-on-edit could not bring into the scene's folder: a save will not carry them. */
export function reportOrphaned(tool: string, orphaned: readonly string[]): void {
    if (orphaned.length === 0) return;
    console.error(`[${tool}] ${orphaned.length} file(s) could not be copied into this scene's folder, so a save will not carry them: ${orphaned.join(', ')}`);
}

/** A viewer refusal as a sentence for the model, with the tool call that gets past it. */
export function refusalText(refusal: Refusal): string {
    const sentence = /[.!?]$/.test(refusal.message) ? refusal.message : `${refusal.message}.`;
    if (refusal.code === 'PLAY_MODE') return `${sentence} Leave play mode with set_play_mode (enabled: false) first.`;
    // Showing Base on the part is the editor's Shared card: the edit then lands
    // on Base, and the page's state stays as authored. The call comes first, so
    // a cut-off result still carries it.
    if (refusal.code === 'STATE_ROUTED' && refusal.pageName && refusal.object) {
        return `First call set_group_state with object "${refusal.object.getPath()}" and state "Base", then edit again. ${sentence}`;
    }
    return sentence;
}

/** The viewer's `PLAY_MODE` refusal, for edits the tools make without its operations. */
const PLAY_MODE_MESSAGE = 'The scene is in play mode, and an edit made now would not survive Stop';

/** The play-mode refusal as a sentence while the scene plays, else null. */
export function playModeRefusalText(ctx: ToolContext): string | null {
    return ctx.traceScene.isPlayMode() ? refusalText({ code: 'PLAY_MODE', message: PLAY_MODE_MESSAGE }) : null;
}

type Execute = ToolDefinition['execute'];

/** A tool that adds or replaces scene content refuses while the scene plays, as the viewer's operations do. */
export function editModeOnly(execute: Execute): Execute {
    return (input, ctx) => {
        const playing = playModeRefusalText(ctx);
        return playing ? Promise.resolve(`Error: ${playing}`) : execute(input, ctx);
    };
}

/** A wiring refusal as a sentence; the prompt calls an event a graph already handles `event-conflict`. */
export function wiringRefusalText(refusal: Refusal): string {
    return refusal.code === 'EVENT_CONFLICT' ? `event-conflict: ${refusalText(refusal)}` : refusalText(refusal);
}

const wiring = new WeakMap<object, Promise<unknown>>();

/**
 * Run the wire tools one at a time per scene. Each reads an object's graph,
 * then writes it after an await, so two calls in parallel on one object would
 * drop one of their changes.
 */
export function oneWiringAtATime<T>(ctx: ToolContext, run: () => Promise<T>): Promise<T> {
    const previous = wiring.get(ctx.traceScene) ?? Promise.resolve();
    const next = previous.then(run);
    wiring.set(ctx.traceScene, next.catch(() => {}));
    return next;
}
